export type PlantId = 'clover' | 'dandelion' | 'poppy' | 'water-lily' | 'blueberry' | 'carrot' | 'potato' | 'onion' | 'pumpkin' | 'watermelon' | 'cucumber'
export type PlantCare = 'water' | 'prune' | 'pest'
/**
 * What kind of plant it is. Each type has its own three care minigames (see
 * `CARE_GAMES` in `care-minigames.ts`): a flower's bee run, a bush's rain cloud
 * and beetle swat, a water plant's dragonfly skim, a root vegetable's barrel
 * climb, bug breakout and trim pong, a vine's pipe spin, caterpillar squirt and
 * runner steer.
 */
export type PlantType = 'flower' | 'bush' | 'water' | 'root' | 'vine'

/** Every care kind, in the order a care marker or a shop list shows them. */
export const PLANT_CARE_KINDS: readonly PlantCare[] = ['water', 'pest', 'prune']
export type PlantSubstrate = 'grass' | 'soil' | 'water' | 'unknown'
export type PlantPlacementFailure = 'out-of-bounds' | 'wrong-substrate' | 'needs-visible-water' | 'too-close' | 'out-of-seeds' | 'tall-grass'

export interface PlantSpecies {
  /** Number of berries on a mature bush and seconds for each picked berry to regrow. */
  readonly fruit?: { readonly count: number; readonly regrowSeconds: number }
  /**
   * Who raids it once it is grown (animal species ids) and how many bites
   * wreck it. A wrecked plant withers for good: it stops counting as grown and
   * waits for the farmer to clear it. A fenced-in plant is safe (see
   * `fencedCells` in `farm-props.ts`). Flowers have none: they are never eaten.
   */
  readonly raid?: { readonly by: readonly string[]; readonly bites: number }
  readonly id: PlantId
  readonly name: string
  readonly subtitle: string
  readonly description: string
  readonly color: string
  readonly substrate: PlantSubstrate
  /** The care-game type: which three minigames this plant plays. */
  readonly careType: PlantType
  readonly spacingRadius: number
  readonly growthSeconds: number
  /**
   * Where in its growth (0..1) the plant stops and asks for something. Care is
   * a short list of moments, not a timer: a few taps per plant, and a plant
   * that has to wait for you just waits. Neglect never kills it; only a
   * raider (`raid`) can.
   */
  readonly care: readonly PlantCareStop[]
  /**
   * Ground cover blends into the lawn instead of standing on it: it needs real
   * turf under it (this much grass coverage, 0..1) and is drawn as a round patch.
   */
  readonly groundCover?: { readonly minCoverage: number }
  /**
   * Care is earned, not tapped: each marker opens a minigame (the games of the
   * species' type) that can be lost and retried. Winning one once earns the
   * species a Green Thumb ribbon for that chore (see `plant-helpers.ts`).
   */
  readonly minigames?: boolean
}

export interface PlantCareStop {
  readonly at: number
  readonly kind: PlantCare
}

export const PLANT_CATALOG: readonly PlantSpecies[] = [
  {
    id: 'clover', name: 'Clover', subtitle: 'Meadow groundcover',
    description: 'A round patch of clover sown into short lawn grass. Water it twice while it spreads; it stays for good. Sheep come for it.',
    color: '#79ad58', substrate: 'grass', careType: 'flower', spacingRadius: 1.05,
    growthSeconds: 80,
    care: [{ at: 0.2, kind: 'water' }, { at: 0.6, kind: 'water' }],
    groundCover: { minCoverage: 0.5 },
    minigames: true,
  },
  {
    id: 'dandelion', name: 'Dandelion', subtitle: 'Sunny lawn weed',
    description: 'A round patch of dandelions sown into short lawn grass. Water it twice while it blooms; it stays for good. Chickens come for it.',
    color: '#e9c545', substrate: 'grass', careType: 'flower', spacingRadius: 1.05,
    growthSeconds: 90,
    care: [{ at: 0.25, kind: 'water' }, { at: 0.65, kind: 'water' }],
    groundCover: { minCoverage: 0.5 },
    minigames: true,
  },
  {
    id: 'poppy', name: 'Poppy', subtitle: 'A bright little flower',
    description: 'Plant in bare soil. Keep it watered, shoo off the aphids, and pinch back one stray shoot.',
    color: '#e77b62', substrate: 'soil', careType: 'flower', spacingRadius: 0.64,
    growthSeconds: 90,
    care: [{ at: 0.25, kind: 'water' }, { at: 0.45, kind: 'pest' }, { at: 0.62, kind: 'prune' }, { at: 0.8, kind: 'water' }],
    minigames: true,
  },
  {
    id: 'water-lily', name: 'Water lily', subtitle: 'Pondside floater',
    description: 'Needs a visible pond, a drink, a shoo for the mosquitoes, and a little pruning as it grows. Geese tear up lily pads: fence the pond in to keep them.',
    color: '#d994c9', substrate: 'water', careType: 'water', spacingRadius: 0.8,
    growthSeconds: 100,
    care: [{ at: 0.28, kind: 'water' }, { at: 0.46, kind: 'pest' }, { at: 0.64, kind: 'prune' }, { at: 0.82, kind: 'water' }],
    minigames: true,
    raid: { by: ['goose'], bites: 3 },
  },
  {
    id: 'blueberry', name: 'Blueberry bush', subtitle: 'A sour-soil berry bush',
    description: 'Plant in bare soil. Call a rain cloud, swat the berry beetles, and snip its wild shoots, and it fruits. A squirrel will strip it bare; fence it in to keep it.',
    color: '#5b73c7', substrate: 'soil', careType: 'bush', spacingRadius: 0.62,
    growthSeconds: 150,
    care: [{ at: 0.22, kind: 'water' }, { at: 0.5, kind: 'pest' }, { at: 0.76, kind: 'prune' }],
    minigames: true,
    fruit: { count: 6, regrowSeconds: 60 },
    // Six berries is one squirrel's whole picnic: taming it costs the bush.
    raid: { by: ['squirrel'], bites: 6 },
  },
  // The root vegetables: what you see is the leafy top and a shoulder of the
  // root pushing up through its bed. Each asks for all three chores, in its own order.
  {
    id: 'carrot', name: 'Carrot', subtitle: 'A sweet orange root',
    description: 'Plant in bare soil. Fetch it water, trim its feathery top, and squash the root bugs, and an orange shoulder pushes up through the bed. Moles dig up roots: fence it in.',
    color: '#ec8a2c', substrate: 'soil', careType: 'root', spacingRadius: 0.56,
    growthSeconds: 110,
    care: [{ at: 0.22, kind: 'water' }, { at: 0.48, kind: 'prune' }, { at: 0.74, kind: 'pest' }],
    minigames: true,
    raid: { by: ['mole'], bites: 3 },
  },
  {
    id: 'potato', name: 'Potato', subtitle: 'A hearty hill of tubers',
    description: 'Plant in bare soil. Squash the striped beetles, fetch it water, and trim its leggy runners, and a hill of potatoes swells underneath. Moles dig up roots: fence it in.',
    color: '#b98a55', substrate: 'soil', careType: 'root', spacingRadius: 0.62,
    growthSeconds: 130,
    care: [{ at: 0.25, kind: 'pest' }, { at: 0.5, kind: 'water' }, { at: 0.76, kind: 'prune' }],
    minigames: true,
    raid: { by: ['mole'], bites: 3 },
  },
  {
    id: 'onion', name: 'Onion', subtitle: 'A red bulb in layers',
    description: 'Plant in bare soil. Fetch it water, shoo off the onion flies, and snap off its flower stalk so the bulb grows fat. Moles dig up roots: fence it in.',
    color: '#a8466e', substrate: 'soil', careType: 'root', spacingRadius: 0.5,
    growthSeconds: 120,
    care: [{ at: 0.3, kind: 'water' }, { at: 0.55, kind: 'pest' }, { at: 0.78, kind: 'prune' }],
    minigames: true,
    raid: { by: ['mole'], bites: 3 },
  },
  // The vines: big leaves on runners that sprawl over the bed (or climb a little
  // trellis), with the fruit swelling underneath. Each asks for all three chores, in its own order.
  {
    id: 'pumpkin', name: 'Pumpkin', subtitle: 'A fat orange autumn gourd',
    description: 'Plant in bare soil. Pipe it water, peg down its runaway runner, and squirt the caterpillars off, and a fat orange pumpkin swells under the leaves. Raccoons raid the patch at night: fence it in.',
    color: '#e8832a', substrate: 'soil', careType: 'vine', spacingRadius: 0.68,
    growthSeconds: 140,
    care: [{ at: 0.22, kind: 'water' }, { at: 0.5, kind: 'prune' }, { at: 0.76, kind: 'pest' }],
    minigames: true,
    raid: { by: ['raccoon'], bites: 4 },
  },
  {
    id: 'watermelon', name: 'Watermelon', subtitle: 'A striped summer melon',
    description: 'Plant in bare soil. Squirt the caterpillars off, pipe it a long drink, and peg down its runaway runner, and a striped melon fattens in the sun. Raccoons raid the patch at night: fence it in.',
    color: '#4f9a4a', substrate: 'soil', careType: 'vine', spacingRadius: 0.68,
    growthSeconds: 150,
    care: [{ at: 0.24, kind: 'pest' }, { at: 0.5, kind: 'water' }, { at: 0.76, kind: 'prune' }],
    minigames: true,
    raid: { by: ['raccoon'], bites: 4 },
  },
  {
    id: 'cucumber', name: 'Cucumber', subtitle: 'A climber on a twig trellis',
    description: 'Plant in bare soil. Peg down its runaway runner, pipe it water, and squirt the caterpillars off, and crisp cucumbers hang from its little trellis. Raccoons raid the patch at night: fence it in.',
    color: '#6fae4a', substrate: 'soil', careType: 'vine', spacingRadius: 0.52,
    growthSeconds: 120,
    care: [{ at: 0.26, kind: 'prune' }, { at: 0.52, kind: 'water' }, { at: 0.78, kind: 'pest' }],
    minigames: true,
    raid: { by: ['raccoon'], bites: 4 },
  },
] as const

/** The shed starts empty: seeds are bought at the shop, never handed out. */
export const STARTING_SEEDS_PER_PLANT = 0

/**
 * The exceptions: one poppy seed, so the first plant is free, and one blueberry
 * seed, so the care minigames can be tried straight away. The poppy grows in bare
 * dirt, as Chapter 1 has no lawn.
 */
export const STARTING_SEED_GIFTS: readonly { readonly species: PlantId; readonly count: number }[] = [
  { species: 'poppy', count: 1 },
  { species: 'blueberry', count: 1 },
]

/** Coins for one seed. Each is a little under what its plant sells for grown. */
export const SEED_PRICES: Readonly<Record<PlantId, number>> = {
  clover: 3,
  dandelion: 3,
  poppy: 5,
  'water-lily': 7,
  blueberry: 12,
  carrot: 6,
  potato: 8,
  onion: 7,
  pumpkin: 9,
  watermelon: 10,
  cucumber: 7,
}
export const PLANT_WATER_MIN_DEPTH = 0.04
const PLANT_SPACING_GAP = 0.12

/**
 * How much room one plant claims around its own centre: its own radius plus
 * half the gap it keeps from a neighbour. The placement preview ring is drawn
 * at exactly this radius so the circle the player sees is the space the rules
 * enforce.
 */
export function plantSpacingExtent(species: PlantId): number {
  return plantSpecies(species).spacingRadius + PLANT_SPACING_GAP / 2
}

export interface PlantSurface {
  readonly substrate: PlantSubstrate
  readonly waterDepth: number
  readonly inBounds: boolean
  /** How thickly grassed the ground is, 0..1. Unset counts as thick enough. */
  readonly coverage?: number
  /**
   * True when the meadow pack has grown the ground past lawn height. Ground
   * cover belongs on short, peaceable grass, so it refuses a meadow.
   */
  readonly tallGrass?: boolean
}

export interface GardenPlant {
  readonly instanceId: number
  readonly species: PlantId
  readonly x: number
  readonly z: number
  readonly growth: number
  /** Grown and not withered: a withered plant no longer counts as grown anywhere. */
  readonly mature: boolean
  /** Raided bare (see `PlantSpecies.raid`): it waits to be cleared and never recovers. */
  readonly withered: boolean
  readonly careNeeded: PlantCare | null
  /** Seconds left until each berry returns; zero is ripe. Non-fruiting plants have none. */
  readonly fruitTimers?: readonly number[]
  readonly ripeFruit?: number
}

export interface PlantPlacementResult {
  readonly valid: boolean
  readonly failure: PlantPlacementFailure | null
}

export interface PlantSimulation {
  readonly plants: readonly GardenPlant[]
  seedsFor(species: PlantId): number
  /** Put bought seeds in the shed; returns the new count. */
  addSeeds(species: PlantId, count?: number): number
  countPlants(species?: PlantId): number
  placementResult(species: PlantId, x: number, z: number, surface: PlantSurface): PlantPlacementResult
  plant(species: PlantId, x: number, z: number, surface: PlantSurface): GardenPlant | null
  /** Pick one ripe berry atomically; returns its slot, or null when none remain. */
  pickFruit(instanceId: number): number | null
  /**
   * One raider's bite, atomic like `pickFruit`: a fruiting plant gives up a ripe
   * berry, any other grown plant a mouthful. False when there is nothing to take.
   * The bite that reaches the species' `raid.bites` withers the plant.
   */
  raid(instanceId: number): boolean
  remove(instanceId: number): GardenPlant | null
  resolveCare(instanceId: number, care: PlantCare): boolean
  tick(deltaSeconds: number): void
  /** Seeds in the shed and every plant in the ground, for saving. */
  exportState(): PlantSimulationState
  /** Replace the garden with a saved one; unknown species and bad numbers are dropped. */
  importState(state: PlantSimulationState): void
}

export interface SavedPlant {
  readonly instanceId: number
  readonly species: PlantId
  readonly x: number
  readonly z: number
  readonly growth: number
  readonly careNeeded: PlantCare | null
  readonly stopsDone: number
  /** Optional for old saves, which load with a full crop. */
  readonly fruitTimers?: readonly number[]
  /** Raiders' bites taken; optional for old saves, which load unbitten. */
  readonly bites?: number
}

export interface PlantSimulationState {
  readonly seeds: Readonly<Record<string, number>>
  readonly plants: readonly SavedPlant[]
  readonly nextInstanceId: number
}

interface MutablePlant {
  readonly instanceId: number
  readonly species: PlantId
  readonly x: number
  readonly z: number
  growth: number
  careNeeded: PlantCare | null
  /** How many of the species' care stops have been answered. */
  stopsDone: number
  fruitTimers: number[]
  bites: number
}

const SPECIES_BY_ID = new Map(PLANT_CATALOG.map((species) => [species.id, species]))

export function plantSpecies(species: PlantId): PlantSpecies {
  const definition = SPECIES_BY_ID.get(species)
  if (!definition) throw new RangeError(`Unknown plant species: ${species}`)
  return definition
}

/** Whether a plant has taken as many bites as its species can stand. */
function isWithered(plant: MutablePlant): boolean {
  const raid = plantSpecies(plant.species).raid
  return raid !== undefined && plant.bites >= raid.bites
}

export function createPlantSimulation(): PlantSimulation {
  const plants: MutablePlant[] = []
  const seedCounts = new Map<PlantId, number>(PLANT_CATALOG.map(({ id }) => [id, STARTING_SEED_GIFTS.find((gift) => gift.species === id)?.count ?? STARTING_SEEDS_PER_PLANT]))
  let nextInstanceId = 1

  function snapshot(plant: MutablePlant): GardenPlant {
    const withered = isWithered(plant)
    const mature = plant.growth >= 1 && !withered
    return {
      instanceId: plant.instanceId,
      species: plant.species,
      x: plant.x,
      z: plant.z,
      growth: plant.growth,
      mature,
      withered,
      careNeeded: plant.careNeeded,
      ...(plantSpecies(plant.species).fruit ? {
        fruitTimers: [...plant.fruitTimers],
        ripeFruit: mature ? plant.fruitTimers.filter((timer) => timer === 0).length : 0,
      } : {}),
    }
  }

  function placementResult(species: PlantId, x: number, z: number, surface: PlantSurface): PlantPlacementResult {
    const definition = plantSpecies(species)
    if (!surface.inBounds) return { valid: false, failure: 'out-of-bounds' }
    if ((seedCounts.get(species) ?? 0) <= 0) return { valid: false, failure: 'out-of-seeds' }
    if (definition.substrate === 'water') {
      if (surface.substrate !== 'water' || surface.waterDepth < PLANT_WATER_MIN_DEPTH) {
        return { valid: false, failure: 'needs-visible-water' }
      }
    } else if (surface.substrate !== definition.substrate) {
      return { valid: false, failure: 'wrong-substrate' }
    } else if (definition.groundCover && (surface.coverage ?? 1) < definition.groundCover.minCoverage) {
      return { valid: false, failure: 'wrong-substrate' }
    } else if (definition.groundCover && surface.tallGrass) {
      return { valid: false, failure: 'tall-grass' }
    }
    const tooClose = plants.some((other) => {
      const otherRadius = plantSpecies(other.species).spacingRadius
      const minimum = definition.spacingRadius + otherRadius + PLANT_SPACING_GAP
      return (other.x - x) ** 2 + (other.z - z) ** 2 < minimum * minimum
    })
    if (tooClose) return { valid: false, failure: 'too-close' }
    return { valid: true, failure: null }
  }

  function pickFruit(instanceId: number): number | null {
    const plant = plants.find((entry) => entry.instanceId === instanceId)
    const fruit = plant && plantSpecies(plant.species).fruit
    if (!plant || !fruit || plant.growth < 1 || isWithered(plant)) return null
    const slot = plant.fruitTimers.findIndex((timer) => timer === 0)
    if (slot < 0) return null
    plant.fruitTimers[slot] = fruit.regrowSeconds
    return slot
  }

  return {
    get plants(): readonly GardenPlant[] {
      return plants.map(snapshot)
    },
    seedsFor(species): number {
      return seedCounts.get(species) ?? 0
    },
    addSeeds(species, count = 1): number {
      const next = (seedCounts.get(species) ?? 0) + Math.max(0, Math.floor(count))
      seedCounts.set(species, next)
      return next
    },
    countPlants(species): number {
      return species === undefined ? plants.length : plants.filter((plant) => plant.species === species).length
    },
    placementResult,
    plant(species, x, z, surface): GardenPlant | null {
      if (!placementResult(species, x, z, surface).valid) return null
      const plant: MutablePlant = {
        instanceId: nextInstanceId++,
        species,
        x,
        z,
        growth: 0.04,
        careNeeded: null,
        stopsDone: 0,
        fruitTimers: Array(plantSpecies(species).fruit?.count ?? 0).fill(0),
        bites: 0,
      }
      plants.push(plant)
      seedCounts.set(species, (seedCounts.get(species) ?? 0) - 1)
      return snapshot(plant)
    },
    pickFruit,
    raid(instanceId): boolean {
      const plant = plants.find((entry) => entry.instanceId === instanceId)
      const definition = plant && plantSpecies(plant.species)
      if (!plant || !definition?.raid || plant.growth < 1 || isWithered(plant)) return false
      if (definition.fruit && pickFruit(instanceId) === null) return false
      plant.bites += 1
      // A withered bush keeps no berries; they never come back.
      if (isWithered(plant)) plant.fruitTimers.fill(definition.fruit?.regrowSeconds ?? 0)
      return true
    },
    remove(instanceId): GardenPlant | null {
      const index = plants.findIndex((plant) => plant.instanceId === instanceId)
      if (index < 0) return null
      return snapshot(plants.splice(index, 1)[0])
    },
    resolveCare(instanceId, care): boolean {
      const plant = plants.find((entry) => entry.instanceId === instanceId)
      if (!plant || plant.growth >= 1 || plant.careNeeded !== care) return false
      plant.careNeeded = null
      plant.stopsDone += 1
      return true
    },
    exportState(): PlantSimulationState {
      return {
        seeds: Object.fromEntries(seedCounts),
        plants: plants.map(({ instanceId, species, x, z, growth, careNeeded, stopsDone, fruitTimers, bites }) => ({ instanceId, species, x, z, growth, careNeeded, stopsDone, fruitTimers: [...fruitTimers], bites })),
        nextInstanceId,
      }
    },
    importState(state): void {
      const finite = (value: unknown, fallback = 0): number => (typeof value === 'number' && Number.isFinite(value) ? value : fallback)
      // A save from before a species existed has no entry for it. Its starting gift
      // (if it has one) is granted then; a saved zero stays zero.
      for (const { id } of PLANT_CATALOG) {
        const gift = STARTING_SEED_GIFTS.find((entry) => entry.species === id)?.count ?? STARTING_SEEDS_PER_PLANT
        seedCounts.set(id, Math.max(0, Math.floor(finite(state.seeds[id], gift))))
      }
      plants.length = 0
      let highest = 0
      for (const saved of state.plants) {
        const definition = SPECIES_BY_ID.get(saved.species)
        if (!definition || !Number.isFinite(saved.instanceId) || plants.some((plant) => plant.instanceId === saved.instanceId)) continue
        const growth = Math.max(0, Math.min(1, finite(saved.growth)))
        plants.push({
          instanceId: saved.instanceId,
          species: saved.species,
          x: finite(saved.x),
          z: finite(saved.z),
          growth,
          careNeeded: growth < 1 && PLANT_CARE_KINDS.includes(saved.careNeeded as PlantCare) ? saved.careNeeded : null,
          stopsDone: Math.max(0, Math.min(definition.care.length, Math.floor(finite(saved.stopsDone)))),
          fruitTimers: Array.from({ length: definition.fruit?.count ?? 0 }, (_, slot) =>
            Math.max(0, Math.min(definition.fruit?.regrowSeconds ?? 0, finite(saved.fruitTimers?.[slot])))),
          bites: Math.max(0, Math.min(definition.raid?.bites ?? 0, Math.floor(finite(saved.bites)))),
        })
        highest = Math.max(highest, saved.instanceId)
      }
      nextInstanceId = Math.max(highest + 1, Math.floor(finite(state.nextInstanceId, 1)))
    },
    tick(deltaSeconds): void {
      const delta = Number.isFinite(deltaSeconds) ? Math.max(0, deltaSeconds) : 0
      if (delta === 0) return
      for (const plant of plants) {
        if (plant.growth >= 1) {
          if (!isWithered(plant)) plant.fruitTimers = plant.fruitTimers.map((timer) => Math.max(0, timer - delta))
          continue
        }
        if (plant.careNeeded) continue
        const definition = plantSpecies(plant.species)
        const stop = definition.care[plant.stopsDone]
        const nextGrowth = Math.min(1, plant.growth + delta / definition.growthSeconds)
        if (stop && nextGrowth >= stop.at) {
          plant.growth = stop.at
          plant.careNeeded = stop.kind
          continue
        }
        plant.growth = nextGrowth
      }
    },
  }
}
